import { useState, useEffect, useCallback, useRef, createContext, useContext } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "motion/react";
import {
  RefreshCw, CalendarDays, Eye, AlertCircle, Settings2, BarChart2,
  FileText, CheckCircle2, Pencil, Check, RotateCcw, Plus,
  LayoutGrid,
} from "lucide-react";
import { getAPI } from "../../../api/frameworksApi";
import { API_URL } from "../../../constants/index";
import { useAuthStore } from "../../../stores/authStore";
import { DataTable, type Column } from "../../components/DataTable";
import { OrgDrillDownDrawer } from "./OrgDrillDownDrawer";
import { GRCDashboardNew } from "../GRCDashboardNew";
import { PipelineWidget, DonutPanel, ConfigureWidgetsMessage } from "../GRCDashboardNew/grcDashboardHelpers";
// Slice colour by rank, shared with the AI companion's donut (and mirrored server-side in
// DashboardChartMapper) so the same organisation is the same colour on both surfaces.
import { contributorColor } from "../../components/charts/donutCore";
import { type PipelineStageItem } from "../GRCDashboardNew/grcDashboardData";
import {
  GROUP_KPIS, COMPLIANCE_CATALOG,
  type GroupKpiItem, type OrgPerformanceRow, type DonutSlice,
} from "./grcGroupData";
import { ConfigurableCardGrid } from "../../components/ConfigurableCardGrid";
import { FitBox } from "../../components/FitBox";
import { AddCardsPanel } from "../LandingDashboard/AddCardsPanel";
import { ExtraCardsPanel } from "../LandingDashboard/ExtraCardsPanel";
import { CardLibraryCarousel } from "../LandingDashboard/CardLibraryCarousel";
import { loadLayout, saveLayout } from "../LandingDashboard/landingStore";
import {
  fetchMyWidgets,
  fetchRoleWidgets,
  enabledCardIds,
  roleDashboardTitle,
  roleDashboardSubtitle,
  GROUP_WIDGET_TO_CARD,
  type MyWidgetsResponse,
  type WidgetKey,
} from "../GRCDashboardNew/grcWidgets";
import { CustomizeWidgetsPanel } from "../GRCDashboardNew/CustomizeWidgetsPanel";
import {
  GRC_CARDS, GRC_CATEGORIES, GRC_LAYOUT_KEY, GRC_CARD_MAP,
} from "./grcDashboardCards";
import {
  Card, SectionTitle, EmptyState, GroupPicker, GroupKpiCard, type ScopeItem,
} from "./grcDashboardWidgets";

/* KPI card metadata only (labels / icons / colours). Values + subtext are
   filled from the live group-summary endpoint — no mock numbers. */
const KPI_TEMPLATE: GroupKpiItem[] = GROUP_KPIS.map((k) => ({ ...k, value: "—", delta: "" }));

/* Temporary static group_id used for the group endpoints until /filter-scope
   returns the real group ids. */
const STATIC_GROUP_ID = "019df865-0e13-74e3-81c0-d08b3221a551";

/* ── Assessment pipeline stage UI (chevron colours + icon) ──────────── */
const PIPELINE_UI: Record<
  string,
  { color: string; bgColor: string; dotColor: string; icon: React.ElementType }
> = {
  "Draft":       { color: "#8b5cf6", bgColor: "#1e1b3a", dotColor: "#8b5cf6", icon: FileText },
  "Planned":     { color: "#4f8ef7", bgColor: "#162040", dotColor: "#4f8ef7", icon: CalendarDays },
  "Not Started": { color: "#94a3b8", bgColor: "#1e2433", dotColor: "#94a3b8", icon: CalendarDays },
  "Approved":    { color: "#4f8ef7", bgColor: "#162040", dotColor: "#4f8ef7", icon: CheckCircle2 },
  "In Progress": { color: "#f97316", bgColor: "#2a1500", dotColor: "#f97316", icon: Settings2 },
  "Review":      { color: "#22d3ee", bgColor: "#082830", dotColor: "#22d3ee", icon: BarChart2 },
  "In Review":   { color: "#22d3ee", bgColor: "#082830", dotColor: "#22d3ee", icon: BarChart2 },
  "Completed":   { color: "#34d399", bgColor: "#082818", dotColor: "#34d399", icon: CheckCircle2 },
  "Overdue":     { color: "#ef4444", bgColor: "#280a0a", dotColor: "#ef4444", icon: AlertCircle },
};

/* Normalise a role string for comparison (lowercase, strip whitespace/hyphens/underscores). */
const normRole = (s: string) => s.toLowerCase().replace(/[\s_-]/g, "");

/* compliance-score → text colour */
function scoreColor(score: number): string {
  if (score >= 90) return "text-emerald-600 dark:text-emerald-400";
  if (score >= 70) return "text-amber-600 dark:text-amber-400";
  return "text-red-600 dark:text-red-400";
}

/* Parse a cell value to a number for sorting (handles 14, "14", "14%").
   Returns null when not numeric so callers fall back to text sort. */
function toSortNumber(v: unknown): number | null {
  if (typeof v === "number") return Number.isNaN(v) ? null : v;
  if (typeof v === "string") {
    const n = parseFloat(v.replace(/[^0-9.-]/g, ""));
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

/* compliance-score → meaningful bar/text colour (hex, theme-agnostic) */
function scoreHex(score: number): string {
  if (score >= 80) return "#10b981"; // good
  if (score >= 60) return "#f59e0b"; // moderate
  if (score >= 40) return "#f97316"; // at risk
  return "#ef4444";                  // critical
}


/* Raw shapes from the group-level endpoints */
interface GroupOrgRow {
  orgId: string; orgName: string;
  applicationCount?: number; activeSspCount?: number;
  openFindingsCount?: number; activeAssessmentsCount?: number;
  complianceScore?: number | null;
}
interface RiskContributorRow { orgId: string; orgName: string; openFindingsCount?: number }
interface PipelineStageRaw { stage: string; count?: number; percent?: number }

/* ══════════════════════════════════════════════════════════════════════
   GRC MANAGER DASHBOARD — Group Level Overview (live data)
   Driven by /dashboard/grc/filter-scope → group picker, then the four
   /dashboard/grc/group/* endpoints scoped to the selected group_id.
   Eye icon on each org row opens the per-org dashboard (old org-level API).
   ══════════════════════════════════════════════════════════════════════ */
/* When the Dashboard is embedded in the Executive Insights tabs, the parent
   provides a DOM slot (in the tab-bar row) to render the header controls
   (Refresh, Group picker, etc.) into, so they share the same row as the tabs.
   Null on the standalone route, where the controls render inline. */
export const DashboardControlsSlotContext = createContext<HTMLElement | null>(null);

export function GRCManagerDashboard({ hideTitle = false }: { hideTitle?: boolean } = {}) {
  const controlsSlot = useContext(DashboardControlsSlotContext);
  /* Header title + subtitle reflect the logged-in user's role
     (e.g. "ISSO Dashboard" with its own compliance description). */
  const userRoles = useAuthStore((s) => s.user?.roles) ?? [];
  const dashboardTitle = roleDashboardTitle(userRoles);
  const dashboardSubtitle = roleDashboardSubtitle(userRoles);
  /* System admins use this same group config-driven dashboard (only the cards
     enabled in Dashboard Config) instead of the per-org "platform" design. */
  const isSystemAdmin = userRoles.some((r) => normRole(r) === "systemadmin");

  const [drillOrg, setDrillOrg] = useState<{ id: string; name: string } | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  /* filter scope → drives which UI to render (group view vs org view) */
  const [scopeReady, setScopeReady] = useState(false);
  const [level, setLevel] = useState<string | null>(null);
  const [groups, setGroups] = useState<ScopeItem[]>([]);
  const [groupId, setGroupId] = useState<string | null>(null);
  const [groupName, setGroupName] = useState<string>("");

  /* live data (empty until the group endpoints respond) */
  const [rows, setRows] = useState<OrgPerformanceRow[]>([]);
  const [kpis, setKpis] = useState<GroupKpiItem[]>(KPI_TEMPLATE);
  const [contributors, setContributors] = useState<DonutSlice[]>([]);
  const [totalFindings, setTotalFindings] = useState<number>(0);
  const [pipelineStages, setPipelineStages] = useState<PipelineStageItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [widgetsResponse, setWidgetsResponse] = useState<MyWidgetsResponse | null>(null);
  const [customizeOpen, setCustomizeOpen] = useState(false);

  /* ── Configurable layout: add / remove / drag-reorder cards ───────── */
  const [layout, setLayout] = useState<string[]>(() => loadLayout(GRC_LAYOUT_KEY, []));
  const [editing, setEditing] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  /* In-page "Extra cards" catalog (selected on top, available below). */
  const [extraOpen, setExtraOpen] = useState(false);
  /* When opening the side panel from a "View detail" tile, expand that card. */
  const [panelCardId, setPanelCardId] = useState<string | undefined>(undefined);
  /* Available cards (side panel / "More cards") = ONLY the role's enabled widgets
     from /my-widgets (the Dashboard Config) plus any always-available extras — not
     the full catalog. The cards shown directly come from the same enabled set.
     When nothing is enabled we show the configure message instead. */
  const [availableIds, setAvailableIds] = useState<string[]>([]);
  /* True when the role has no enabled widgets → render the configure message. */
  const [widgetsDisabled, setWidgetsDisabled] = useState(false);
  const defaultIdsRef = useRef<string[]>([]);
  /* Admin-chosen card ids for the user's role(s) — from the role-widgets API.
     Drives the "Add cards" universe so OFF cards still appear there. */
  const adminUniverseRef = useRef<string[]>([]);
  const hadSavedLayout = useRef<boolean>(
    typeof window !== "undefined" && localStorage.getItem(GRC_LAYOUT_KEY) !== null,
  );
  /* Single mutation point for membership/order - keeps state + storage in sync. */
  const commitLayout = (ids: string[]) => { setLayout(ids); saveLayout(GRC_LAYOUT_KEY, ids); };
  const toggleCard = (id: string) =>
    commitLayout(layout.includes(id) ? layout.filter((x) => x !== id) : [...layout, id]);
  const removeCard = (id: string) => commitLayout(layout.filter((x) => x !== id));
  /* Drag-reorder: the visible card order IS the layout. Preserve any ids not in
     the catalog (defensive) by appending them after the reordered cards. */
  const reorderCards = (ids: string[]) => {
    const extras = layout.filter((id) => !ids.includes(id));
    commitLayout([...ids, ...extras]);
  };
  const resetLayout = () => commitLayout([...defaultIdsRef.current]);
  const cards = layout.filter((id) => GRC_CARD_MAP[id]);
  /* Catalog cards available to add (drives More / Extra / Explore). */
  const availableCards = GRC_CARDS.filter((c) => availableIds.includes(c.id));

  const updateWidgets = useCallback((response: MyWidgetsResponse) => {
    setWidgetsResponse(response);
    const roles = response.roles ?? [];
    const enabledSet = new Set<WidgetKey>();   // personal ON = the Dashboard-Config enabled set
    const universeSet = new Set<WidgetKey>();  // everything the admin chose for the role (on + off)
    for (const role of roles) {
      for (const w of role.widgets ?? []) {
        universeSet.add(w.widget);
        if (w.enabled) enabledSet.add(w.widget);
      }
    }
    const enabled = Array.from(enabledSet);

    const isCard = (id: string) => !!GRC_CARD_MAP[id];
    /* Add / Extra / Explore offer the admin-chosen universe (role-widgets API) so
       a card turned Off still appears there (to add) instead of disappearing; the
       grid itself shows the personally-enabled subset. Fall back to the my-widgets
       universe if the role-widgets fetch returned nothing. */
    const universeCardIds = adminUniverseRef.current.length
      ? adminUniverseRef.current
      : enabledCardIds(Array.from(universeSet), GROUP_WIDGET_TO_CARD, isCard).ids;
    const universeIds = universeCardIds.filter(isCard);
    const allDisabled = universeIds.length === 0;
    const { ids: enabledIds } = enabledCardIds(enabled, GROUP_WIDGET_TO_CARD, isCard);
    setWidgetsDisabled(allDisabled);
    setAvailableIds(universeIds);
    defaultIdsRef.current = [...enabledIds];

    if (allDisabled) {
      commitLayout([]);
      hadSavedLayout.current = true;
    } else {
      setLayout((prevLayout) => {
        const keep = prevLayout.filter((id) => enabledIds.includes(id));
        const added = enabledIds.filter((id) => !keep.includes(id));
        const next = [...keep, ...added];
        saveLayout(GRC_LAYOUT_KEY, next);
        return next;
      });
      hadSavedLayout.current = true;
    }
  }, []);

  /* ── Enabled widgets (drive both the panel list and the grid) ────────────
     available = the role's enabled widgets (+ extras), so the "Add cards" panel
     only offers what the Dashboard Config enabled; the layout shown directly is
     the same enabled set. When nothing is enabled → configure message. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        /* my-widgets = personal on/off; roles list lets us resolve each role's id
           so we can read the admin's full selection (on + off) from role-widgets. */
        const [response, rolesRes] = await Promise.all([
          fetchMyWidgets(),
          getAPI(API_URL.PLATFORM_ROLES + "/all"),
        ]);
        if (cancelled) return;
        const rolesList: Array<{ id: string; name?: string; displayName?: string }> = rolesRes?.data ?? [];
        const norm = (s: string) => s.toLowerCase().replace(/[\s_-]/g, "");
        /* (orgId, roleId) per role the user has → admin universe = union of their
           role-widgets selections (so Off cards still surface in Add/Explore). */
        const pairs = (response.roles ?? [])
          .map((b) => ({
            orgId: b.orgUnitId,
            roleId: rolesList.find((r) => norm(r.displayName ?? r.name ?? "") === norm(b.roleName ?? ""))?.id,
          }))
          .filter((p): p is { orgId: string; roleId: string } => !!p.orgId && !!p.roleId);
        const lists = await Promise.all(
          pairs.map((p) => fetchRoleWidgets(p.orgId, p.roleId).catch(() => [])),
        );
        if (cancelled) return;
        const universe = new Set<WidgetKey>();
        lists.forEach((l) => l.forEach((w) => universe.add(w.widget)));
        adminUniverseRef.current = enabledCardIds(
          Array.from(universe), GROUP_WIDGET_TO_CARD, (id) => !!GRC_CARD_MAP[id],
        ).ids;
        updateWidgets(response);
      } catch {
        if (cancelled) return;
        /* No config data → show nothing (not the whole catalog mock). */
        setWidgetsDisabled(true);
        setAvailableIds([]);
        defaultIdsRef.current = [];
        commitLayout([]);
        hadSavedLayout.current = true;
      }
    })();
    return () => { cancelled = true; };
  }, [updateWidgets]);

  /* ── Group-level data for the selected group_id ──────────────────── */
  const fetchGroupData = useCallback(async (gid: string) => {
    setLoading(true);
    setError(null);
    try {
      const [sumRes, orgsRes, riskRes, pipeRes] = await Promise.all([
        getAPI(`${API_URL.GRC_GROUP_SUMMARY}?group_id=${gid}`),
        getAPI(`${API_URL.GRC_GROUP_ORGS}?group_id=${gid}`, 0),
        getAPI(`${API_URL.GRC_GROUP_TOP_RISK}?group_id=${gid}&limit=4`),
        getAPI(`${API_URL.GRC_GROUP_PIPELINE}?group_id=${gid}`),
      ]);

      const s = sumRes?.data ?? {};
      const orgRows: GroupOrgRow[] = orgsRes?.data?.rows ?? [];
      const riskRows: RiskContributorRow[] = riskRes?.data?.rows ?? [];
      const pipe = pipeRes?.data ?? {};

      /* ── KPI row ── */
      const compScore = s.groupComplianceScore;
      setKpis((prev) =>
        prev.map((k) => {
          if (k.id === "total_orgs")          return { ...k, value: s.totalOrganizations ?? 0 };
          if (k.id === "active_ssps")         return { ...k, value: s.activePublishedSsps ?? 0 };
          if (k.id === "group_compliance")    return { ...k, value: compScore == null ? "—" : `${Math.round(compScore)}%` };
          if (k.id === "high_risk_orgs")      return { ...k, value: s.highRiskApplicationsCount ?? 0 };
          if (k.id === "open_findings")       return { ...k, value: s.openFindingsCount ?? 0 };
          if (k.id === "active_assessments")  return { ...k, value: s.activeAssessmentsCount ?? pipe.totalAssessments ?? 0 };
          return k;
        }),
      );

      /* ── Organization Performance table ── */
      const liveRows: OrgPerformanceRow[] = orgRows.map((r) => ({
        id: r.orgId,
        name: r.orgName,
        applications: r.applicationCount ?? 0,
        ssps: r.activeSspCount ?? 0,
        activeAssessments: r.activeAssessmentsCount ?? 0,
        openFindings: r.openFindingsCount ?? 0,
        complianceScore: r.complianceScore == null ? 0 : Math.round(r.complianceScore),
      }));
      setRows(liveRows);

      /* ── Top Risk Contributors — show EVERY org (incl. 0 findings) ──
         Start from the dedicated risk ranking, then append any org that the
         risk endpoint left out so the legend lists all organisations. */
      const groupFindings =
        s.openFindingsCount ?? liveRows.reduce((a, r) => a + r.openFindings, 0);
      const denom = groupFindings || 1;

      const seen = new Set<string>();
      const contribOrgs: { label: string; value: number }[] = [];
      riskRows.forEach((r) => {
        seen.add(r.orgId);
        contribOrgs.push({ label: r.orgName, value: r.openFindingsCount ?? 0 });
      });
      liveRows.forEach((r) => {
        if (!seen.has(r.id)) contribOrgs.push({ label: r.name, value: r.openFindings });
      });

      const slices: DonutSlice[] = contribOrgs
        .sort((a, b) => b.value - a.value)
        .map((o, i) => ({
          label: o.label,
          value: o.value,
          pct: Math.round((o.value / denom) * 100),
          color: contributorColor(i),
        }));
      setContributors(slices);
      setTotalFindings(groupFindings);

      /* ── Assessment pipeline (dedicated endpoint) ──
         Keep the API's stage order — do not re-sort. */
      const stagesRaw: PipelineStageRaw[] = pipe.stages ?? [];
      const stages: PipelineStageItem[] = stagesRaw.map((st) => {
        const ui = PIPELINE_UI[st.stage] ?? PIPELINE_UI["Planned"];
        return {
          id: st.stage.toLowerCase().replace(/\s+/g, "_"),
          label: st.stage,
          count: st.count ?? 0,
          pct: Math.round(st.percent ?? 0),
          color: ui.color,
          bgColor: ui.bgColor,
          dotColor: ui.dotColor,
          icon: ui.icon,
        };
      });
      setPipelineStages(stages);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "";
      if (msg !== "OFFLINE") {
        setError("Could not load group data. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  /* ── Filter scope → resolve the group picker, then load first group ─ */
  const fetchScope = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Call the real filter-scope API endpoint
      const scopeRes = await getAPI(API_URL.GRC_FILTER_SCOPE);
      const data = scopeRes?.data ?? { level: null, items: [] };

      setLevel(data.level);
      setGroups(data.items);

      // System admins always use the group overview (config-driven cards), never
      // the per-org "platform" dashboard — so handle them with the GROUP path.
      // If ORGANIZATION/DIVISION/DEPARTMENT/APPLICATION level, set the first item and mark ready to show org dashboard
      // If GROUP level, load the group overview
      if (!isSystemAdmin && data.level && data.level !== "GROUP" && data.items.length > 0) {
        setGroupId(data.items[0].id);
        setGroupName(data.items[0].name);
        setScopeReady(true);
        setLoading(false);
      } else if ((data.level === "GROUP" || isSystemAdmin) && data.items.length > 0) {
        setGroupId(data.items[0].id);
        setGroupName(data.items[0].name);
        await fetchGroupData(data.items[0].id);
        setScopeReady(true);
      } else {
        // No access or invalid scope
        setScopeReady(true);
        setLoading(false);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "";
      if (msg !== "OFFLINE") {
        setError("Could not load dashboard scope. Please try again.");
      }
      setScopeReady(true);
      setLoading(false);
    }
  }, [fetchGroupData, isSystemAdmin]);

  useEffect(() => { fetchScope(); }, [fetchScope]);

  const onSelectGroup = (it: ScopeItem) => {
    if (it.id === groupId) return;
    setGroupId(it.id);
    setGroupName(it.name);
    fetchGroupData(it.id);
  };

  const refresh = () => (groupId ? fetchGroupData(groupId) : fetchScope());

  const openOrg = (row: OrgPerformanceRow) => {
    setDrillOrg({ id: row.id, name: row.name });
    setDrawerOpen(true);
  };

  /* ── Organization Performance table columns ─────────────────────── */
  /* ── Sorting for the org-performance table (client-side, controlled) ── */
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };
  const sortedRows = sortKey
    ? [...rows].sort((a, b) => {
        const av = a[sortKey as keyof OrgPerformanceRow];
        const bv = b[sortKey as keyof OrgPerformanceRow];
        /* Compliance Score (and any numeric/percentage column) sorts by value. */
        const an = toSortNumber(av);
        const bn = toSortNumber(bv);
        let cmp: number;
        if (an !== null && bn !== null) cmp = an - bn;
        else cmp = String(av ?? "").localeCompare(String(bv ?? ""), undefined, { numeric: true });
        return sortDir === "asc" ? cmp : -cmp;
      })
    : rows;

  const columns: Column<OrgPerformanceRow>[] = [
    {
      key: "name",
      header: "Organization",
      align: "left",
      headerAlign: "center",
      sortable: true,
      render: (row) => (
        <button
          onClick={() => openOrg(row)}
          className="text-left text-sm font-medium text-foreground transition-colors hover:text-indigo-600 dark:hover:text-indigo-400"
        >
          {row.name}
        </button>
      ),
    },
    {
      key: "applications",
      header: "Applications",
      sortable: true,
      render: (row) => (
        <span className="text-sm font-semibold tabular-nums text-foreground/80">{row.applications}</span>
      ),
    },
    {
      key: "ssps",
      header: "SSPs",
      sortable: true,
      render: (row) => (
        <span className="text-sm font-semibold tabular-nums text-foreground/80">{row.ssps}</span>
      ),
    },
    {
      key: "activeAssessments",
      header: "Approved Assessments",
      sortable: true,
      render: (row) => (
        <span className="text-sm font-semibold tabular-nums text-foreground/80">{row.activeAssessments}</span>
      ),
    },
    {
      key: "openFindings",
      header: "Open Findings",
      sortable: true,
      render: (row) => (
        <span className="text-sm font-semibold tabular-nums text-foreground/80">{row.openFindings}</span>
      ),
    },
    {
      key: "complianceScore",
      header: "Compliance Score",
      sortable: true,
      render: (row) => {
        const c = scoreHex(row.complianceScore);
        return (
          <div className="mx-auto flex w-28 flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <span className="rounded bg-primary/20 px-1.5 py-0.5 text-[9px] font-bold tracking-wide text-primary ring-1 ring-inset ring-primary/30">
                {COMPLIANCE_CATALOG}
              </span>
              <span className="text-sm font-bold tabular-nums" style={{ color: c }}>
                {row.complianceScore}%
              </span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-foreground/10">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${row.complianceScore}%`, background: c }}
              />
            </div>
          </div>
        );
      },
    },
    {
      key: "action",
      header: "Action",
      render: (row) => (
        <button
          onClick={() => openOrg(row)}
          title={`View ${row.name} dashboard`}
          className="inline-flex size-7 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground transition-colors hover:border-indigo-300 hover:text-indigo-600 dark:hover:border-indigo-500/40 dark:hover:text-indigo-400"
        >
          <Eye className="size-3.5" />
        </button>
      ),
    },
  ];

  /* ── Live JSX for every configurable card (keyed by card id) ──────── */
  const kpiNode = (id: string) => {
    const kpi = kpis.find((k) => k.id === id);
    return kpi ? <GroupKpiCard kpi={kpi} index={0} /> : null;
  };
  const renderMap: Record<string, React.ReactNode> = {
    total_orgs: kpiNode("total_orgs"),
    active_ssps: kpiNode("active_ssps"),
    group_compliance: kpiNode("group_compliance"),
    high_risk_orgs: kpiNode("high_risk_orgs"),
    open_findings: kpiNode("open_findings"),
    active_assessments: kpiNode("active_assessments"),
    assessment_status: (
      <Card className="flex h-full flex-col">
        <SectionTitle title="Assessment Status Overview" subtitle="Active assessments by stage" />
        <div className="min-h-0 flex-1">
          {pipelineStages.some((s) => s.count > 0) ? (
            <FitBox designWidth={640} maxScale={1.4}>
              <PipelineWidget
                id="assessment-status-overview-chart"
                name="Assessment Status Overview"
                stages={pipelineStages}
              />
            </FitBox>
          ) : (
            <EmptyState loading={loading} label="No assessment data" />
          )}
        </div>
      </Card>
    ),
    top_risk_contributors: (
      <Card className="flex h-full flex-col">
        <SectionTitle title="Top Risk Contributors" subtitle="Share of total open findings" />
        <div className="min-h-0 flex-1">
          {contributors.length ? (
            <DonutPanel
              id="top-risk-contributors-chart"
              name="Top Risk Contributors"
              slices={contributors}
              centerValue={totalFindings}
              centerLabel="Total Findings"
              legendPerPage={2}
            />
          ) : (
            <EmptyState loading={loading} label="No open findings" />
          )}
        </div>
      </Card>
    ),
    org_performance: (
      <Card className="overflow-hidden">
        <SectionTitle
          title="Organization Performance Overview"
          subtitle="Click anywhere on a row to drill into its dashboard"
        />
        {/* Height adapts to the data: with few orgs the card shrinks to fit;
            past 5 rows it caps here and the rest scroll inside the table. */}
        <DataTable<OrgPerformanceRow>
          columns={columns}
          data={sortedRows}
          rowKey={(r) => r.id}
          onRowClick={(row) => openOrg(row)}
          emptyText="No Data Available"
          maxHeight="320px"
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={handleSort}
        />
      </Card>
    ),
  };

  /* ── Scope-driven view selection ─────────────────────────────────────
     Wait for /filter-scope, then:
       • level === "GROUP"          → this group overview (below)
       • anything else (ORG / …)    → render the org dashboard as the page  */
  if (!scopeReady) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <RefreshCw className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Non-GROUP scope (ORGANIZATION / DIVISION / DEPARTMENT / APPLICATION) →
  // render the per-org dashboard directly as the main page. System admins are
  // excluded: they always use the group config-driven dashboard below.
  if (level && level !== "GROUP" && !isSystemAdmin) {
    return <GRCDashboardNew />;
  }

  return (
    <>
      <AnimatePresence>
        <motion.div
          className="space-y-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.25 }}
        >
          {/* ══ HEADER ════════════════════════════════════════════════ */}
          {(() => {
          const controls = (
            <div className="flex flex-wrap items-center gap-2">
              {/* Customize button hidden
              {!editing && (
                <button
                  type="button"
                  onClick={() => setCustomizeOpen(true)}
                  className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border bg-card px-3 text-xs font-semibold text-foreground/80 transition-colors hover:border-indigo-400/40 hover:text-foreground"
                >
                  <SlidersHorizontal className="size-3.5" />
                  Customize
                </button>
              )}
              */}
            {!widgetsDisabled && (editing ? (
                <>
                  <button
                    type="button"
                    onClick={resetLayout}
                    className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border bg-transparent px-3 text-xs font-semibold text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground"
                  >
                    <RotateCcw className="size-3.5" />
                    Reset
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(false)}
                    className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-indigo-600 px-4 text-xs font-semibold text-white transition-colors hover:bg-indigo-700"
                  >
                    <Check className="size-3.5" />
                    Done
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setExtraOpen((o) => !o)}
                    className={`inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold transition-colors ${
                      extraOpen
                        ? "border-indigo-500/50 bg-indigo-500/[0.08] text-foreground"
                        : "border-border bg-card text-foreground/80 hover:border-indigo-400/40 hover:text-foreground"
                    }`}
                  >
                    <LayoutGrid className="size-3.5" />
                    Manage Dashboards
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(true)}
                    className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border bg-card px-3 text-xs font-semibold text-foreground/80 transition-colors hover:border-indigo-400/40 hover:text-foreground"
                  >
                    <Pencil className="size-3.5" />
                    Edit layout
                  </button>
                </>
              ))}
              <button
                onClick={refresh}
                disabled={loading}
                className="inline-flex h-9 items-center justify-center rounded-lg bg-indigo-600 px-2 text-white transition-colors hover:bg-indigo-700 disabled:opacity-60"
                title="Refresh data"
              >
                <RefreshCw className={`size-4 ${loading ? "animate-spin" : ""}`} />
              </button>
              <GroupPicker
                items={groups}
                selectedId={groupId}
                selectedName={groupName}
                onSelect={onSelectGroup}
              />
            </div>
          );
          /* Embedded (a slot is provided): portal the controls into the parent's
             tab-bar row. Otherwise render the full header inline. */
          if (hideTitle && controlsSlot) return createPortal(controls, controlsSlot);
          return (
            <motion.div
              className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between"
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {hideTitle ? (
                <div />
              ) : (
                <div>
                  <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
                    {dashboardTitle}
                  </h1>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {dashboardSubtitle}
                  </p>
                </div>
              )}
              {controls}
            </motion.div>
          );
          })()}

          {/* ══ ERROR BANNER ══════════════════════════════════════════ */}
          <AnimatePresence>
            {error && (
              <motion.div
                className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/[0.08] px-3 py-2.5 text-sm text-amber-600 dark:text-amber-400"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
              >
                <AlertCircle className="size-4 shrink-0" />
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          {/* ══ EDIT-MODE BANNER ══════════════════════════════════════ */}
          {editing && (
            <div className="flex items-center gap-2 rounded-lg border border-indigo-500/20 bg-indigo-500/[0.06] px-3 py-2 text-xs font-medium text-indigo-600 dark:text-indigo-400">
              <Pencil className="size-3.5 shrink-0" />
              Editing layout - drag the handle on each card to move it; cards reflow to fill the space.
            </div>
          )}

          {/* ══ CONFIGURE MESSAGE / EXTRA-CARDS / CONFIGURABLE CARD GRID  */}
          {widgetsDisabled ? (
            <ConfigureWidgetsMessage />
          ) : extraOpen ? (
            <ExtraCardsPanel
              active={layout}
              cards={availableCards}
              renderMap={renderMap}
              sizeOf={(id) => GRC_CARD_MAP[id]?.size}
              onApply={(ids) => commitLayout(ids)}
              onClose={() => { setExtraOpen(false); setPanelCardId(undefined); }}
              highlightId={panelCardId}
              onClearHighlight={() => setPanelCardId(undefined)}
            />
          ) : cards.length === 0 ? (
            <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border">
              <p className="text-sm font-semibold text-foreground">Shape Your Perfect Dashboard</p>
              <p className="max-w-xs text-center text-xs text-muted-foreground">
                Add the cards and charts that matter most to you and build a workspace that works the way you do.
              </p>
              <button
                type="button"
                onClick={() => setPanelOpen(true)}
                className="mt-1 inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-indigo-700"
              >
                <Plus className="size-3.5" />
                Add cards
              </button>
            </div>
          ) : (
            <ConfigurableCardGrid
              items={cards}
              renderMap={renderMap}
              editing={editing}
              onRemove={removeCard}
              onReorder={reorderCards}
              sizeOf={(id) => GRC_CARD_MAP[id]?.size}
            />
          )}

          {/* ══ EXPLORE CARDS CAROUSEL ════════════════════════════════ */}
          {!widgetsDisabled && !extraOpen && (
            <CardLibraryCarousel
              title="Explore cards"
              subtitle="Cards you haven't added yet. View details to add one to your dashboard."
              cards={availableCards.filter((c) => !layout.includes(c.id))}
              onViewDetail={(id) => {
                /* Open the Manage-dashboard panel with that card spotlighted. */
                setPanelOpen(false);
                setPanelCardId(id);
                setExtraOpen(true);
              }}
            />
          )}
        </motion.div>
      </AnimatePresence>

      {/* ══ DRILL-DOWN SIDE PANEL (old UI) ══════════════════════════ */}
      <OrgDrillDownDrawer org={drillOrg} open={drawerOpen} onOpenChange={setDrawerOpen} />

      {/* ══ ADD-CARDS SIDE PANEL ════════════════════════════════════ */}
      <AddCardsPanel
        open={panelOpen}
        active={layout}
        cards={availableCards}
        categories={GRC_CATEGORIES}
        onToggle={toggleCard}
        onClose={() => { setPanelOpen(false); setPanelCardId(undefined); }}
        onEdit={() => {
          setPanelOpen(false);
          setPanelCardId(undefined);
          setEditing(true);
        }}
        renderPreview={(id) => renderMap[id]}
        initialExpandedId={panelCardId}
      />
      <CustomizeWidgetsPanel
        open={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
        widgetsResponse={widgetsResponse}
        onWidgetsChanged={updateWidgets}
      />
    </>
  );
}

